import 'Components/atoms/vm-icon-next/vm-icon-next';
import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { dismissPoll, submitPollInteraction } from 'Api/polls';
import Styles from './vm-poll.scss';

export interface PollOption {
    id: number;
    text: string;
}

@customElement('vm-poll')
export class VmPoll extends LitElement {
    static override styles = Styles;

    // --- Data properties (set by parent vm-poll-overlay) ---

    @property({ type: Number, attribute: 'assignment-id' })
    assignmentId!: number;

    @property({ type: Number, attribute: 'poll-id' })
    pollId!: number;

    @property({ type: String })
    question: string = '';

    @property({ type: Array })
    options: PollOption[] = [];

    // Placement mode set by the parent overlay — controls visual variant.
    // "overlay" = desktop card floating over video, "sheet" = mobile bottom sheet.
    @property({ type: String, reflect: true })
    mode: 'overlay' | 'sheet' = 'overlay';

    // --- Internal reactive state ---

    @state()
    private _selectedOptionId: number | null = null;

    @state()
    private _submitting: boolean = false;

    private async _handleOptionClick(option: PollOption, index: number) {
        if (this._submitting || this._selectedOptionId !== null) return;

        this._selectedOptionId = option.id;
        this._submitting = true;

        // Tell the overlay to cancel the auto-close timer
        this.dispatchEvent(new CustomEvent('poll-submitting', { bubbles: true, composed: true }));

        try {
            await submitPollInteraction(this.assignmentId, { option_id: option.id, shown_position: index });
        } catch {
            this._selectedOptionId = null;
            this._submitting = false;
            this.dispatchEvent(
                new CustomEvent('poll-error', {
                    bubbles: true,
                    composed: true,
                    detail: { assignmentId: this.assignmentId },
                }),
            );
            return;
        }

        this.dispatchEvent(
            new CustomEvent('poll-responded', {
                bubbles: true,
                composed: true,
                detail: { assignmentId: this.assignmentId, optionId: option.id, shownPosition: index },
            }),
        );
    }

    private _handleDismiss() {
        if (this._submitting) return;
        this._submitting = true;

        // Fire-and-forget — close the UI immediately, don't wait for the API
        dismissPoll(this.assignmentId).catch(() => {});

        this.dispatchEvent(
            new CustomEvent('poll-dismissed', {
                bubbles: true,
                composed: true,
                detail: { assignmentId: this.assignmentId },
            }),
        );
    }

    private _renderOption(option: PollOption, index: number) {
        const isSelected = this._selectedOptionId === option.id;

        return html`
            <button
                class="poll-option ${isSelected ? 'poll-option--selected' : ''}"
                data-testid="poll-option"
                @click=${() => this._handleOptionClick(option, index)}
                ?disabled=${this._submitting}
                aria-label="${option.text}"
            >
                <span class="poll-option__text" data-testid="poll-option-text">${option.text}</span>
                <span
                    class="poll-option__radio ${isSelected ? 'poll-option__radio--selected' : ''}"
                    data-testid="poll-option-radio"
                >
                    ${isSelected
                        ? html`<span class="poll-option__radio-dot" data-testid="poll-option-radio-dot"></span>`
                        : nothing}
                </span>
            </button>
        `;
    }

    override render() {
        return html`
            <div class="poll" data-testid="poll">
                <div class="poll__header" data-testid="poll-header">
                    <span class="poll__title" data-testid="poll-title">Poll</span>
                    <button
                        class="poll__close"
                        data-testid="poll-close"
                        @click=${this._handleDismiss}
                        aria-label="Close poll"
                    >
                        <vm-icon-next name="x-close"></vm-icon-next>
                    </button>
                </div>
                <div class="poll__question" data-testid="poll-question">${this.question}</div>
                <div class="poll__options" data-testid="poll-options">
                    ${this.options.map((opt, i) => this._renderOption(opt, i))}
                </div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-poll': VmPoll;
    }
}
